import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import { useAuth } from '@/app/AuthProvider';
import { isSuperAdmin } from '@/lib/permissions';
import {
  ITEM_KIND_COLORS,
  partitionItemKinds,
  useCreateItemKind,
  useDeleteItemKind,
  useItemKinds,
  type ItemKindColor,
  type ItemKindRow,
  type ItemKindSelection,
} from '@/features/inventory/hooks';

/**
 * Inline-editable type cell shared by the items grid and the item
 * detail page.
 *
 * Renders every type the item belongs to as coloured badges. Clicking
 * them opens a dropdown of checkboxes — one per type in the org's
 * catalog — so an item can be, for example, both a finished good and
 * a sub-assembly:
 *
 *   * a check mark next to every type the item currently belongs to;
 *   * toggling a box saves immediately and leaves the menu open;
 *   * a "+ Create new type" footer for super admins, with an inline
 *     name field and colour swatch picker — the new type is minted
 *     in the catalog AND checked on the current item in one shot;
 *   * a trash button on non-system rows for super admins so they
 *     can prune stale custom types.
 *
 * Read-only viewers (non-admins) see the badges alone with no edit
 * affordance. Admins (write access on inventory) can check existing
 * types; only super admins can mint new ones, matching the RLS policy
 * on `public.item_kinds` (migration 41).
 */
export interface KindPickerProps {
  /** Every type slug currently assigned to the item. */
  value: string[];
  /**
   * Slug to keep as `items.kind` while it stays checked. Pass the
   * item's current primary kind.
   */
  preferredPrimary?: string | null;
  /** Persist the full checkbox selection. */
  onSave: (next: ItemKindSelection) => Promise<void> | void;
  /** False renders the badges with no edit affordance. */
  canEdit: boolean;
  /** "cell" tightens the trigger for table use; "kv" relaxes it for the detail page. */
  density?: 'cell' | 'kv';
  /** Aligns the trigger contents (the detail page right-aligns its KV rows). */
  align?: 'left' | 'right';
  /** Extra classes on the trigger (the read-only chrome). */
  triggerClassName?: string;
  className?: string;
}

export function KindPicker({
  value,
  preferredPrimary,
  onSave,
  canEdit,
  density = 'cell',
  align = 'left',
  triggerClassName,
  className,
}: KindPickerProps) {
  const { role } = useAuth();
  const isSuper = isSuperAdmin(role);

  const { data: catalog, isLoading: catalogLoading } = useItemKinds();
  const createKind = useCreateItemKind();
  const deleteKind = useDeleteItemKind();

  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draftLabel, setDraftLabel] = useState('');
  const [draftColor, setDraftColor] = useState<ItemKindColor>('slate');
  const [pendingSelect, setPendingSelect] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset transient state every time the menu reopens so a previous
  // create-form draft / colour choice doesn't bleed across edits.
  useEffect(() => {
    if (!open) {
      setCreating(false);
      setDraftLabel('');
    } else {
      setDraftColor(pickNextColor(catalog ?? []));
    }
  }, [open, catalog]);

  // The autofocus prop on the input doesn't fire reliably inside the
  // Radix portal on first mount; explicitly nudge it.
  useEffect(() => {
    if (creating) {
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [creating]);

  // Sort defensively even though the hook orders by (position, label) —
  // a future caller could pass an unsorted catalog through and we
  // still want the dropdown to read top-to-bottom.
  const sortedKinds = useMemo(() => {
    return [...(catalog ?? [])].sort((a, b) => {
      if (a.position !== b.position) return a.position - b.position;
      return a.label.localeCompare(b.label);
    });
  }, [catalog]);

  const selected = useMemo(() => {
    const seen = new Set<string>();
    const slugs: string[] = [];
    for (const slug of value) {
      if (!slug || seen.has(slug)) continue;
      seen.add(slug);
      slugs.push(slug);
    }
    return slugs;
  }, [value]);

  const orderedSlugs = useMemo(() => sortedKinds.map((k) => k.slug), [sortedKinds]);

  const badges = (
    <span className={cn('flex flex-wrap gap-1', align === 'right' && 'justify-end')}>
      {selected.length === 0 ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        [...selected]
          .sort((a, b) => {
            const ai = orderedSlugs.indexOf(a);
            const bi = orderedSlugs.indexOf(b);
            const ar = ai < 0 ? Number.MAX_SAFE_INTEGER : ai;
            const br = bi < 0 ? Number.MAX_SAFE_INTEGER : bi;
            return ar - br || a.localeCompare(b);
          })
          .map((slug) => (
            <KindBadge
              key={slug}
              row={sortedKinds.find((k) => k.slug === slug) ?? null}
              fallbackSlug={slug}
            />
          ))
      )}
    </span>
  );

  const persist = async (slugs: string[]) => {
    const next = partitionItemKinds(slugs, preferredPrimary ?? selected[0], orderedSlugs);
    setPendingSelect(true);
    try {
      await onSave(next);
    } finally {
      setPendingSelect(false);
    }
  };

  const toggleKind = async (slug: string) => {
    const next = selected.includes(slug) ? selected.filter((s) => s !== slug) : [...selected, slug];
    if (next.length === 0) {
      toast.error('An item needs at least one type.');
      return;
    }
    try {
      await persist(next);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't update types.");
    }
  };

  const submitCreate = async () => {
    const label = draftLabel.trim();
    if (!label) {
      toast.error('Give the new type a name.');
      return;
    }
    try {
      const created = await createKind.mutateAsync({ label, color: draftColor });
      // Check the new type on this item as well as minting it. The
      // operator opened the menu from a specific row, so leaving the
      // new type unchecked would hide the thing they just created.
      try {
        await persist([...selected, created.slug]);
      } catch (assignErr) {
        toast.error(
          assignErr instanceof Error
            ? assignErr.message
            : 'Created the type, but failed to assign it to this item.',
        );
      }
      setCreating(false);
      setDraftLabel('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create type.');
    }
  };

  const removeKind = async (kind: ItemKindRow) => {
    const ok = confirm(
      `Delete the “${kind.label}” type for the whole organisation? It can only be removed if no items still use it.`,
    );
    if (!ok) return;
    try {
      await deleteKind.mutateAsync({ id: kind.id });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete type.');
    }
  };

  // ---------------- Read-only render ----------------
  if (!canEdit) {
    return <span className={cn('inline-flex', className)}>{badges}</span>;
  }

  // ---------------- Editable render ----------------
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Edit type"
          title="Edit type"
          onClick={(e) => e.stopPropagation()}
          onMouseDownCapture={(e) => e.stopPropagation()}
          className={cn(
            // Same hover affordance as DisplayShell in EditableItemField
            // so the kind cell looks identical to every other inline-
            // editable column.
            'group inline-flex w-full items-center gap-1.5 rounded-md border border-transparent px-1.5 py-0.5 text-left text-foreground transition-colors',
            'hover:border-border hover:bg-secondary/60 focus-visible:border-ring focus-visible:bg-secondary/60 focus-visible:outline-none',
            align === 'right' && 'justify-end',
            density === 'cell' ? '-mx-1.5 -my-0.5' : 'min-h-[28px]',
            triggerClassName,
            className,
          )}
        >
          <span className={cn('flex-1', align === 'right' && 'text-right')}>{badges}</span>
          <Pencil
            className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            aria-hidden
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={align === 'right' ? 'end' : 'start'}
        className="w-72 p-0"
        onClick={(e) => e.stopPropagation()}
        onMouseDownCapture={(e) => e.stopPropagation()}
      >
        <p className="border-b border-border px-3 py-2 text-xs text-muted-foreground">
          Select every type this item belongs to.
        </p>
        <div className="max-h-72 overflow-y-auto p-1">
          {catalogLoading ? (
            <div className="px-3 py-4 text-center text-xs text-muted-foreground">Loading…</div>
          ) : sortedKinds.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              No types yet. {isSuper ? 'Create one below.' : 'Ask a super admin to add one.'}
            </div>
          ) : (
            sortedKinds.map((kind) => {
              const checked = selected.includes(kind.slug);
              return (
                <div
                  key={kind.id}
                  className="group flex w-full items-center gap-2 rounded-md px-2 py-1 hover:bg-secondary/60"
                >
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={checked}
                    disabled={pendingSelect}
                    onClick={() => void toggleKind(kind.slug)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left disabled:opacity-60"
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border',
                        checked
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'opacity-60',
                      )}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    <KindBadge row={kind} />
                    {kind.is_system && (
                      <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
                        Built-in
                      </span>
                    )}
                  </button>
                  {isSuper && !kind.is_system && (
                    <button
                      type="button"
                      onClick={() => void removeKind(kind)}
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                      aria-label={`Delete ${kind.label} type`}
                      title="Delete type"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        {isSuper && (
          <div className="border-t border-border p-2">
            {creating ? (
              <div className="space-y-2">
                <p className="px-1 text-xs text-muted-foreground">Create new type</p>
                <input
                  ref={inputRef}
                  value={draftLabel}
                  onChange={(e) => setDraftLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void submitCreate();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setCreating(false);
                      setDraftLabel('');
                    }
                  }}
                  placeholder="e.g. Service, Spare part…"
                  maxLength={64}
                  className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <div className="flex flex-wrap items-center gap-1.5 px-1">
                  {ITEM_KIND_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setDraftColor(c)}
                      aria-label={`Use ${c} colour`}
                      title={c}
                      className={cn(
                        'h-5 w-5 rounded-full border-2 transition-transform hover:scale-110',
                        kindSwatchClass(c),
                        draftColor === c ? 'border-foreground' : 'border-transparent',
                      )}
                    />
                  ))}
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setCreating(false);
                      setDraftLabel('');
                    }}
                    className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void submitCreate()}
                    disabled={createKind.isPending || !draftLabel.trim()}
                    className="rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {createKind.isPending ? 'Creating…' : 'Create & select'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Create new type…</span>
              </button>
            )}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// =====================================================================
// Sub-components
// =====================================================================

/**
 * Coloured badge for an item kind. Falls back to a muted "Unknown"
 * pill when the slug isn't in the catalog (e.g. an item still using a
 * legacy slug after a kind was deleted out from under it).
 */
export function KindBadge({
  row,
  fallbackSlug,
}: {
  row: ItemKindRow | null;
  fallbackSlug?: string;
}) {
  if (row) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium',
          kindBadgeClass(row.color),
        )}
      >
        <span className="max-w-[14rem] truncate">{row.label}</span>
      </span>
    );
  }
  return (
    <Badge variant="muted">
      <span className="max-w-[14rem] truncate">{fallbackSlug ?? '—'}</span>
    </Badge>
  );
}

// =====================================================================
// Colour token → Tailwind class maps
//
// Mirrors the equivalents in TagsPicker so kind / tag badges read as
// part of the same design system. Tailwind needs literal classnames
// in the source for its JIT to pick them up, so we list the full set
// here rather than computing them.
// =====================================================================

export function kindBadgeClass(color: ItemKindColor): string {
  switch (color) {
    case 'slate':
      return 'border-slate-200 bg-slate-100 text-slate-700';
    case 'red':
      return 'border-red-200 bg-red-100 text-red-700';
    case 'orange':
      return 'border-orange-200 bg-orange-100 text-orange-700';
    case 'amber':
      return 'border-amber-200 bg-amber-100 text-amber-800';
    case 'green':
      return 'border-green-200 bg-green-100 text-green-700';
    case 'teal':
      return 'border-teal-200 bg-teal-100 text-teal-700';
    case 'blue':
      return 'border-blue-200 bg-blue-100 text-blue-700';
    case 'indigo':
      return 'border-indigo-200 bg-indigo-100 text-indigo-700';
    case 'purple':
      return 'border-purple-200 bg-purple-100 text-purple-700';
    case 'pink':
      return 'border-pink-200 bg-pink-100 text-pink-700';
    default:
      return 'border-slate-200 bg-slate-100 text-slate-700';
  }
}

function kindSwatchClass(color: ItemKindColor): string {
  switch (color) {
    case 'slate':
      return 'bg-slate-400';
    case 'red':
      return 'bg-red-400';
    case 'orange':
      return 'bg-orange-400';
    case 'amber':
      return 'bg-amber-400';
    case 'green':
      return 'bg-green-400';
    case 'teal':
      return 'bg-teal-400';
    case 'blue':
      return 'bg-blue-400';
    case 'indigo':
      return 'bg-indigo-400';
    case 'purple':
      return 'bg-purple-400';
    case 'pink':
      return 'bg-pink-400';
    default:
      return 'bg-slate-400';
  }
}

/**
 * Default colour for the "create new kind" form. Rotates through the
 * palette based on how many kinds already exist so the operator
 * isn't always staring at the same swatch.
 */
function pickNextColor(existing: ItemKindRow[]): ItemKindColor {
  return ITEM_KIND_COLORS[existing.length % ITEM_KIND_COLORS.length];
}
